/** The document and folder names the backend read out of this conversation's documents tools, and
 * the rehype pass that marks every mention of them in a reply so it can lead with its type icon. */

import type { IChatDocumentMention } from './chat-document-mentions.types';

export const CHAT_DOCUMENT_MENTIONS_PART_TYPE = 'data-document-mentions';

export const CHAT_DOCUMENT_KIND_ATTRIBUTE = 'data-chat-document';

export const CHAT_DOCUMENT_FILE_NAME_ATTRIBUTE = 'data-file-name';

interface IHastNode {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: IHastNode[];
}

interface IMessageLike {
  parts: ReadonlyArray<{ type: string; data?: unknown }>;
}

const SKIPPED_TAGS = new Set(['a', 'code', 'pre', 'script', 'style']);

const INLINE_WRAPPER_TAGS = new Set(['strong', 'em', 'b', 'i']);

const WHOLE_ITEM_TAGS = new Set(['td', 'th', 'li']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isHastNode = (value: unknown): value is IHastNode =>
  isRecord(value) && typeof value.type === 'string';

const isBlankText = (node: IHastNode): boolean =>
  node.type === 'text' && !(node.value ?? '').trim();

const meaningfulChildren = (node: IHastNode): IHastNode[] =>
  (node.children ?? []).filter((child) => !isBlankText(child));

function readMention(value: unknown): IChatDocumentMention | null {
  if (!isRecord(value) || typeof value.name !== 'string' || !value.name)
    return null;
  if (value.kind !== 'folder' && value.kind !== 'document') return null;

  return {
    name: value.name,
    kind: value.kind,
    fileName: typeof value.fileName === 'string' ? value.fileName : null,
  };
}

export class ChatDocumentMentionsHelper {
  /** Every mention the conversation's documents tools returned; a later read of a name replaces an
   * earlier one, so a document uploaded since shows its file type. */
  static collect(
    messages: ReadonlyArray<IMessageLike>
  ): IChatDocumentMention[] {
    const byName = new Map<string, IChatDocumentMention>();
    messages.forEach((message) =>
      message.parts.forEach((part) => {
        if (part.type !== CHAT_DOCUMENT_MENTIONS_PART_TYPE) return;
        const mentions = isRecord(part.data) ? part.data.mentions : null;
        if (!Array.isArray(mentions)) return;
        mentions.forEach((raw) => {
          const mention = readMention(raw);
          if (mention) byName.set(mention.name, mention);
        });
      })
    );

    return [...byName.values()];
  }

  /** Marks mentions in place. A name of two or more words is matched anywhere in prose, exactly and
   * case-sensitively; a one-word name ("Other", "Bylaws") only when it is a whole table cell or list
   * item, so ordinary words in a sentence are left alone. Links and code are never touched. */
  static transformer(mentions: ReadonlyArray<IChatDocumentMention>) {
    const byName = new Map(mentions.map((mention) => [mention.name, mention]));
    const phrases = mentions
      .map((mention) => mention.name)
      .filter((name) => /\s/.test(name))
      .sort((a, b) => b.length - a.length);
    const phrasePattern = phrases.length
      ? new RegExp(
          `(?<![\\p{L}\\p{N}])(?:${phrases.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}])`,
          'gu'
        )
      : null;

    const toElement = (text: string): IHastNode => {
      const mention = byName.get(text);

      return {
        type: 'element',
        tagName: 'span',
        properties: {
          [CHAT_DOCUMENT_KIND_ATTRIBUTE]: mention?.kind ?? 'document',
          [CHAT_DOCUMENT_FILE_NAME_ATTRIBUTE]: mention?.fileName ?? '',
        },
        children: [{ type: 'text', value: text }],
      };
    };

    const isWholeItem = (ancestors: IHastNode[]): boolean => {
      for (let index = ancestors.length - 1; index >= 0; index -= 1) {
        const ancestor = ancestors[index];
        if (!ancestor || meaningfulChildren(ancestor).length !== 1)
          return false;
        if (WHOLE_ITEM_TAGS.has(ancestor.tagName ?? '')) return true;
        if (ancestor.tagName === 'p')
          return ancestors[index - 1]?.tagName === 'li';
        if (!INLINE_WRAPPER_TAGS.has(ancestor.tagName ?? '')) return false;
      }

      return false;
    };

    const splitText = (
      node: IHastNode,
      ancestors: IHastNode[]
    ): IHastNode[] => {
      const value = node.value ?? '';
      const trimmed = value.trim();
      const wholeTextMention = byName.get(trimmed);
      if (wholeTextMention && !/\s/.test(trimmed) && isWholeItem(ancestors)) {
        const start = value.indexOf(trimmed);
        return [
          { type: 'text', value: value.slice(0, start) },
          toElement(trimmed),
          { type: 'text', value: value.slice(start + trimmed.length) },
        ].filter((part) => part.type !== 'text' || part.value);
      }
      if (!phrasePattern) return [node];

      const parts: IHastNode[] = [];
      let last = 0;
      Array.from(value.matchAll(phrasePattern)).forEach((match) => {
        const start = match.index ?? 0;
        if (start > last)
          parts.push({ type: 'text', value: value.slice(last, start) });
        parts.push(toElement(match[0]));
        last = start + match[0].length;
      });
      if (parts.length === 0) return [node];
      if (last < value.length)
        parts.push({ type: 'text', value: value.slice(last) });

      return parts;
    };

    const transform = (node: IHastNode, ancestors: IHastNode[]): IHastNode => {
      if (!node.children || SKIPPED_TAGS.has(node.tagName ?? '')) return node;
      if (node.properties?.[CHAT_DOCUMENT_KIND_ATTRIBUTE] !== undefined)
        return node;
      const path = [...ancestors, node];

      return {
        ...node,
        children: node.children.flatMap((child) =>
          child.type === 'text'
            ? splitText(child, path)
            : [transform(child, path)]
        ),
      };
    };

    return (tree: unknown): IHastNode | undefined =>
      byName.size > 0 && isHastNode(tree) ? transform(tree, []) : undefined;
  }
}

/** The rehype plugin. Mentions travel as its options because streamdown caches a processor by each
 * plugin's name and options: a closure over them would keep serving the first, empty, list. */
export function rehypeChatDocumentMentions(options: {
  mentions: ReadonlyArray<IChatDocumentMention>;
}) {
  return ChatDocumentMentionsHelper.transformer(options.mentions);
}
